import React, { useEffect, useState } from 'react';
import { Button, Col, Table } from 'react-bootstrap';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import { requestList as request } from 'api/standard/code';
import ActionButton from 'components/common/ActionButton';
import { onListClick } from 'function/event';
import { getText } from 'function/language';
import { getLoginInform } from 'function/login';
import { getErrorMessage } from 'function/message';
import { RESPONSE_CODE } from 'types/const';
import { RETURN_YN } from 'types/type';
import comma from 'utils/format/comma';
import format from 'utils/string/format';

/**
 * 기준 정보 > 속성 코드 관리 - 목록
 */
const CodeTable = ({
  load,
  order,
  parent,
  onLoad,
  onSelect,
  onSettingClick
}) => {
  /* variable */
  const { haccpYn } = getLoginInform();

  /* state */
  // 목록
  const [datas, setDatas] = useState([]);
  // 로딩 여부
  const [isLoading, setIsLoading] = useState(true);
  // 메시지
  const [message, setMessage] = useState({});
  // 선택한 번호
  const [select, setSelect] = useState(0);

  /* watch */
  useEffect(() => {
    if (load === true) {
      requestList();
    }
  }, [load]);
  useEffect(() => {
    if (isLoading === false) {
      onLoad?.();
    }
  }, [isLoading]);

  /* method */
  // 코드 목록 조회 요청
  const requestList = async () => {
    setIsLoading(true);
    setDatas([]);
    setMessage({});

    try {
      const {
        list = [],
        code,
        message
      } = await request({
        codeGroup: parent,
        [haccpYn === RETURN_YN.Y ? 'haccpYn' : 'mixYn']: RETURN_YN.Y
      });

      if (code !== RESPONSE_CODE.Success) {
        throw {
          code,
          error: message
        };
      }

      setDatas(list);
    } catch (error) {
      setMessage(error);
    } finally {
      setIsLoading(false);
    }
  };

  /* event */
  // 목록 선택 시
  const onSelectList = (inform = {}) => {
    setSelect(inform.seq);
    onSelect(inform);
  };

  /* view */
  return (
    <Col sm={4}>
      <Table bordered hover responsive className="fs--1 text-center">
        <thead className="bg-200 text-700 align-middle border-300">
          <tr>
            {order === 0 ? (
              <th colSpan={3}>{getText('masterCode')}</th>
            ) : (
              <th className="position-relative" colSpan={4}>
                {format(getText('orderCode'), { order: comma(order) })}
                {order > 0 && (
                  <Button
                    size="sm"
                    className="position-absolute top-50 end-0 translate-middle-y me-2"
                    onClick={() => {
                      onSettingClick({ codeGroup: parent }, order);
                    }}
                  >
                    + {getText('insert')}
                  </Button>
                )}
              </th>
            )}
          </tr>
          <tr>
            <th>{getText('code')}</th>
            <th>{getText('codeNmKr')}</th>
            <th>{getText('useYn')}</th>
            {order > 0 && <th>{getText('update')}</th>}
          </tr>
        </thead>
        <tbody>
          {datas.length > 0 ? (
            datas.map(value => (
              <tr
                key={`datas${value.seq}`}
                className={
                  select === value.seq
                    ? 'bg-300'
                    : value.useYn === RETURN_YN.Y
                    ? 'bg-soft-success'
                    : 'bg-200'
                }
                onClick={event => {
                  onListClick(event, () => onSelectList(value));
                }}
              >
                {/* 1차 코드 이하만 선택 가능 */}
                <td
                  className={classNames({
                    'cursor-pointer': order < 2
                  })}
                >
                  {value.codeCd}
                </td>
                <td
                  className={classNames({
                    'cursor-pointer': order < 2
                  })}
                >
                  {value.codeNmKr}
                </td>
                <td
                  className={classNames({
                    'cursor-pointer': order < 2
                  })}
                >
                  {value.useYn}
                </td>
                {order > 0 && (
                  <td>
                    <ActionButton
                      title={getText('update')}
                      icon="edit"
                      variant="action"
                      className="p-0 lh-1"
                      onClick={() => {
                        onSettingClick(value, order);
                      }}
                    />
                  </td>
                )}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={order === 0 ? 3 : 4}>
                {isLoading
                  ? getText('loading')
                  : message.code
                  ? getErrorMessage(message.code, message.error)
                  : getErrorMessage(RESPONSE_CODE.NotFoundData)}
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </Col>
  );
};

CodeTable.propTypes = {
  // 로드 여부
  load: PropTypes.bool,

  // 차수
  order: PropTypes.number,

  // 상위 코드
  parent: PropTypes.string,

  // 로드 시 이벤트
  onLoad: PropTypes.func,

  // 선택 시 이벤트
  onSelect: PropTypes.func,

  // 등록/수정 클릭 시 이벤트
  onSettingClick: PropTypes.func
};
CodeTable.defaultProps = {
  load: true,
  order: 0
};

export default CodeTable;
